import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Menu, X, Phone, Mail, MapPin, ChevronRight, ImagePlus, Volume2, Speaker, Lightbulb, Mic, MonitorSpeaker, Zap } from "lucide-react";
import AdminPage from "@/AdminPage";
import PackagesPage from "@/PackagesPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const EQUIPMENT = [
  { key: "speakers", label: "Speakers", desc: "PA systems & subwoofers for any venue size", icon: Speaker, image: "https://images.unsplash.com/photo-1669434113533-e39391413a18?w=600&q=80" },
  { key: "stage", label: "Stage", desc: "Portable stages, risers & platforms", icon: MonitorSpeaker, image: "https://images.unsplash.com/photo-1764448102359-48dcfeca1c4b?w=600&q=80" },
  { key: "lighting", label: "Lighting", desc: "LED fixtures, spots & moving heads", icon: Lightbulb, image: "https://images.unsplash.com/photo-1758306120745-a2fb7b34b6b3?w=600&q=80" },
  { key: "mic", label: "Microphones", desc: "Wired, wireless & lavalier mics", icon: Mic, image: "https://images.unsplash.com/photo-1769509068789-f242b5a6fc47?w=600&q=80" },
  { key: "stand", label: "Stands", desc: "Mic stands, speaker stands & tripods", icon: Volume2, image: "https://images.unsplash.com/photo-1772399764232-c35bac262ed6?w=600&q=80" },
  { key: "mixer", label: "Mixers", desc: "Analog & digital mixing consoles", icon: MonitorSpeaker, image: "https://images.unsplash.com/photo-1630965764686-159c575031b3?w=600&q=80" },
  { key: "generator", label: "Generators", desc: "Portable power for outdoor events", icon: Zap, image: "https://images.unsplash.com/photo-1570513927207-1bbbd1aa5b10?w=600&q=80" },
  { key: "fans", label: "Fans", desc: "Industrial & event cooling fans", icon: Volume2, image: "https://images.unsplash.com/photo-1762786482010-ec48a030871a?w=600&q=80" },
  { key: "marquee", label: "Marquee", desc: "Event marquees & tent structures", icon: MonitorSpeaker, image: "https://images.unsplash.com/photo-1519226612673-73c0234437ef?w=600&q=80" },
  { key: "cables", label: "Cables", desc: "Audio, power & signal cables", icon: Volume2, image: "https://images.unsplash.com/photo-1678272141992-88c7a0a34585?w=600&q=80" },
];

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <header className="nav-header" role="banner">
      <nav className="nav-container" aria-label="Main navigation">
        <Link to="/" className="nav-logo" data-testid="nav-logo">Eloquent Voices</Link>
        <button className="nav-toggle" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} aria-label="Toggle navigation menu">
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <ul className={`nav-links ${isOpen ? 'open' : ''}`}>
          <li><a href="#equipment" className="nav-link" data-testid="nav-equipment">Equipment</a></li>
          <li><Link to="/packages" className="nav-link" data-testid="nav-packages">Packages</Link></li>
          <li><a href="#about" className="nav-link" data-testid="nav-about">About</a></li>
          <li><a href="#gallery" className="nav-link" data-testid="nav-gallery">Gallery</a></li>
          <li><a href="#contact" className="nav-link" data-testid="nav-contact">Contact</a></li>
        </ul>
      </nav>
    </header>
  );
}

function HeroSection() {
  return (
    <section className="hero-section" id="hero" aria-labelledby="hero-heading">
      <div className="hero-content">
        <div className="hero-text animate-fade-in-up">
          <p className="subtitle">Professional Audio Equipment Rental</p>
          <h1 id="hero-heading">Eloquent Voices</h1>
          <p className="tagline">Premium sound, stage, and lighting equipment for events of every scale. From intimate gatherings to large-scale productions, we deliver the gear that makes your event unforgettable.</p>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <a href="#equipment"><Button className="btn-primary" data-testid="browse-equipment-btn">Browse Equipment<ChevronRight className="ml-2" size={18} /></Button></a>
            <a href="#contact"><Button className="btn-secondary" style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'white' }} data-testid="get-quote-btn">Get a Quote</Button></a>
            <Link to="/packages"><Button className="btn-secondary" style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'white' }} data-testid="see-packages-btn">See our Packages</Button></Link>
          </div>
        </div>
        <div className="hero-image animate-fade-in-up animate-delay-200">
          <img src="https://images.unsplash.com/photo-1635350963882-aa424ca750bb?w=700&q=80" alt="Professional audio mixing console" loading="eager" />
        </div>
      </div>
    </section>
  );
}

function EquipmentCard({ item, index }) {
  const Icon = item.icon;
  return (
    <a href={`#gallery-${item.key}`} style={{ textDecoration: 'none' }}>
      <div className={`equipment-card animate-fade-in-up animate-delay-${(index + 1) * 100}`} data-testid={`equipment-card-${item.key}`}>
        {item.image ? (
          <img src={item.image} alt={item.label} loading="lazy" />
        ) : (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #0F5132, #198754)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={48} color="rgba(255,255,255,0.3)" />
          </div>
        )}
        <div className="equipment-card-overlay">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <Icon size={18} color="rgba(255,255,255,0.8)" />
            <h3>{item.label}</h3>
          </div>
          <p>{item.desc}</p>
        </div>
      </div>
    </a>
  );
}

function EquipmentSection() {
  return (
    <section className="section" id="equipment" aria-labelledby="equipment-heading">
      <div className="container">
        <h2 id="equipment-heading" className="section-heading">Our Equipment</h2>
        <p className="section-description">Everything you need to produce a world-class event, available for hire with flexible rental periods and professional support.</p>
        <div className="equipment-grid">{EQUIPMENT.map((item, i) => <EquipmentCard key={item.key} item={item} index={i} />)}</div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section className="about-section" id="about" aria-labelledby="about-heading">
      <div className="about-content">
        <h2 id="about-heading" className="section-heading">About Eloquent Voices</h2>
        <blockquote>
          <p>Eloquent Voices is a professional audio equipment rental service built for event organisers, production companies, and anyone who needs top-tier sound and staging. We supply speakers, PA systems, microphones, mixers, lighting rigs, stages, and portable generators for weddings, corporate events, concerts, festivals, and private parties.</p>
          <br />
          <p>Our inventory is meticulously maintained and regularly upgraded to ensure you always have access to reliable, high-performance gear. Whether you need a compact setup for an intimate gathering or a full production rig for a large-scale event, we have you covered.</p>
          <br />
          <p>We pride ourselves on competitive rates, flexible hire periods, and expert advice. Our team can help you choose the right equipment for your venue and audience, and we offer delivery, setup, and collection services to take the hassle out of your event planning.</p>
        </blockquote>
      </div>
    </section>
  );
}

function GalleryImageItem({ image, title }) {
  return (
    <div className="gallery-item" data-testid={`gallery-item-${image.id}`}>
      <img src={getImageUrl(image.image_url)} alt={image.caption || title} loading="lazy" />
      {image.caption && <div className="gallery-caption"><p>{image.caption}</p></div>}
    </div>
  );
}

function GallerySubSection({ sectionKey, title, images }) {
  const hasImages = images && images.length > 0;
  return (
    <div id={`gallery-${sectionKey}`} style={{ marginBottom: '48px' }} data-testid={`gallery-section-${sectionKey}`}>
      <h3 style={{ fontSize: '1.4rem', color: 'var(--primary)', marginBottom: '16px', fontFamily: "'Space Grotesk', sans-serif" }}>{title}</h3>
      {hasImages ? (
        <div className="gallery-grid">{images.map((img) => <GalleryImageItem key={img.id} image={img} title={title} />)}</div>
      ) : (
        <div className="gallery-empty"><ImagePlus size={40} color="var(--text-light)" /><p style={{ marginTop: '12px', color: 'var(--text-muted)' }}>Photos coming soon</p></div>
      )}
    </div>
  );
}

function GallerySection({ galleries }) {
  return (
    <section className="section section-alt" id="gallery" aria-labelledby="gallery-heading">
      <div className="container">
        <h2 id="gallery-heading" className="section-heading">Gallery</h2>
        <p className="section-description">Browse our equipment in action.</p>
        {EQUIPMENT.map((eq) => <GallerySubSection key={eq.key} sectionKey={eq.key} title={eq.label} images={galleries[eq.key] || []} />)}
      </div>
    </section>
  );
}

function ContactSection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [finishTime, setFinishTime] = useState('');
  const [location, setLocation] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [peopleAttending, setPeopleAttending] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleEquipment = (key) => {
    setSelectedEquipment(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedEquipment.length === 0) { toast.error('Please select at least one equipment type'); return; }
    setIsSubmitting(true);
    const equipLabels = selectedEquipment.map(k => EQUIPMENT.find(eq => eq.key === k)?.label || k).join(', ');
    const message = `Quote Request\n\nEvent Start Date: ${eventDate}${endDate ? `\nEvent End Date: ${endDate}` : ''}\nStart Time: ${startTime}\nFinish Time: ${finishTime}\nLocation: ${location}\nEquipment Needed: ${equipLabels}\nPeople Attending: ${peopleAttending}\nPhone: ${phone}\n\nAdditional Info: ${additionalInfo}`;
    try {
      await axios.post(`${API}/contact`, { name, email, message });
      toast.success("Quote request sent! We'll get back to you shortly.");
      setName(''); setEmail(''); setPhone(''); setEventDate(''); setEndDate(''); setStartTime(''); setFinishTime('');
      setLocation(''); setSelectedEquipment([]); setPeopleAttending(''); setAdditionalInfo('');
    } catch { toast.error('Failed to send request.'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <section className="contact-section" id="contact" aria-labelledby="contact-heading">
      <div className="container">
        <h2 id="contact-heading" className="section-heading">Get in Touch</h2>
        <p className="section-description">Fill in your event details and we'll get back to you within 24 hours.</p>
        <div className="contact-grid">
          <ContactInfo />
          <Card>
            <CardHeader><CardTitle>Get a Quote</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group"><Label className="form-label">Your Name</Label><Input type="text" value={name} onChange={(e) => setName(e.target.value)} required data-testid="quote-name-input" /></div>
                  <div className="form-group"><Label className="form-label">Email Address</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="quote-email-input" /></div>
                </div>
                <div className="form-group"><Label className="form-label">Phone Number</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="quote-phone-input" /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group"><Label className="form-label">Event Start Date</Label><Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} required data-testid="quote-date-input" /></div>
                  <div className="form-group"><Label className="form-label">End Date <span style={{ fontWeight: 400, color: 'var(--text-light)', fontSize: '0.85rem' }}>(optional)</span></Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="quote-end-date-input" /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group"><Label className="form-label">Start Time</Label><Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required data-testid="quote-start-input" /></div>
                  <div className="form-group"><Label className="form-label">Finish Time</Label><Input type="time" value={finishTime} onChange={(e) => setFinishTime(e.target.value)} required data-testid="quote-finish-input" /></div>
                </div>
                <div className="form-group"><Label className="form-label">Event Location</Label><Input type="text" value={location} onChange={(e) => setLocation(e.target.value)} required placeholder="Venue name and address" data-testid="quote-location-input" /></div>
                <div className="form-group">
                  <Label className="form-label">Equipment Needed</Label>
                  <EquipmentCheckboxes selected={selectedEquipment} onToggle={toggleEquipment} />
                </div>
                <div className="form-group"><Label className="form-label">People Attending</Label><Input type="number" value={peopleAttending} onChange={(e) => setPeopleAttending(e.target.value)} required placeholder="Approx. number" data-testid="quote-people-input" /></div>
                <div className="form-group"><Label className="form-label">Additional Information</Label><Textarea value={additionalInfo} onChange={(e) => setAdditionalInfo(e.target.value)} rows={3} placeholder="Any other details about your event..." data-testid="quote-info-input" /></div>
                <Button type="submit" className="btn-primary" disabled={isSubmitting} data-testid="quote-submit-btn">{isSubmitting ? 'Sending...' : 'Request Quote'}</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function ContactInfo() {
  return (
    <div className="contact-info">
      <h3>Contact Information</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Phone size={20} color="var(--primary)" />
          <div>
            <p style={{ fontWeight: 600, marginBottom: '2px', fontSize: '0.95rem' }}>Phone</p>
            <a href="tel:07354695536" style={{ color: 'var(--primary)', fontWeight: 600, fontSize: '1.1rem', textDecoration: 'none' }} data-testid="phone-link">07354 695 536</a>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Mail size={20} color="var(--primary)" />
          <div>
            <p style={{ fontWeight: 600, marginBottom: '2px', fontSize: '0.95rem' }}>Email</p>
            <a href="mailto:eloquentvoicesuk@gmail.com" style={{ color: 'var(--primary)', textDecoration: 'none' }} data-testid="email-link">eloquentvoicesuk@gmail.com</a>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <MapPin size={20} color="var(--primary)" />
          <div>
            <p style={{ fontWeight: 600, marginBottom: '2px', fontSize: '0.95rem' }}>Location</p>
            <p style={{ color: 'var(--text-muted)', margin: 0 }}>London, United Kingdom</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function EquipmentCheckboxes({ selected, onToggle }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '6px' }}>
      {EQUIPMENT.map((eq) => {
        const checked = selected.includes(eq.key);
        return (
          <label key={eq.key} className={`equip-checkbox ${checked ? 'checked' : ''}`} data-testid={`quote-equip-${eq.key}`}>
            <input type="checkbox" checked={checked} onChange={() => onToggle(eq.key)} />
            {eq.label}
          </label>
        );
      })}
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer" role="contentinfo">
      <div className="container">
        <p className="footer-logo">Eloquent Voices</p>
        <p>Professional Audio Equipment Rental</p>
        <p style={{ marginTop: '12px', fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>London, United Kingdom</p>
        <p style={{ marginTop: '20px', fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>&copy; {new Date().getFullYear()} Eloquent Voices. All rights reserved.</p>
      </div>
    </footer>
  );
}

function HomePage() {
  const [galleries, setGalleries] = useState({});
  useEffect(() => { axios.get(`${API}/galleries`).then(res => setGalleries(res.data)).catch(() => {}); }, []);
  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Navigation />
      <main id="main-content">
        <HeroSection />
        <EquipmentSection />
        <AboutSection />
        <GallerySection galleries={galleries} />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Toaster position="bottom-right" richColors />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/packages" element={<PackagesPage Navigation={Navigation} Footer={Footer} />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
